import React, { useState } from "react";
import { supabase } from "./auth/supabase";
import { useAuth } from "./auth/AuthContext";

/* ============================================================
   Newsletter signup form.
   Anyone (logged in or not) can subscribe.
   Writes to public.newsletter_subscribers via Supabase.
   ============================================================ */
export default function NewsletterForm({ variant = "footer", source = "footer" }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [msg, setMsg] = useState("");

  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!validEmail(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }

    setStatus("sending");
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: email.trim().toLowerCase(),
      source,
      user_id: user?.id || null,
    });

    if (error) {
      // 23505 = unique-violation — they're already subscribed. Treat that as success.
      if (error.code === "23505" || /duplicate|unique/i.test(error.message)) {
        setStatus("done");
        setMsg("You're already on the list — thanks!");
        return;
      }
      setStatus("error");
      setMsg(error.message || "Something went wrong. Try again.");
      return;
    }

    setStatus("done");
    setMsg("Subscribed. We'll be in touch.");
  };

  if (status === "done") {
    return (
      <div className={`newsletter newsletter-${variant} newsletter-done`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12l5 5L20 7" />
        </svg>
        <span>{msg}</span>
      </div>
    );
  }

  return (
    <form className={`newsletter newsletter-${variant}`} onSubmit={handleSubmit} noValidate>
      <div className="newsletter-row">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@somewhere.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Your email"
          disabled={status === "sending"}
        />
        <button type="submit" disabled={status === "sending"}>
          {status === "sending" ? "…" : "Subscribe"}
        </button>
      </div>
      {msg && status === "error" && <p className="newsletter-error">{msg}</p>}
      <p className="newsletter-hint">
        One short email a week. Unsubscribe any time.
      </p>
    </form>
  );
}
